'use client';

import { useState, useEffect, useRef, memo } from 'react';
import { useMarketStream } from '@/hooks/useMarketStream';
import GlassCard from '@/components/GlassCard';
import MiniSparkline from './MiniSparkline';
import { motion, AnimatePresence } from 'framer-motion';

interface MarketData {
  symbol: string;
  price: number;
  change: number;
  changePercent: number;
  volume: number;
  high: number;
  low: number;
  open: number;
  previousClose: number;
  sparkline: number[];
}

interface LiveMarketDataProps {
  symbols: string[];
  onSelectAsset?: (symbol: string) => void;
}

// Memoized Asset Card for maximum performance
const AssetCard = memo(({ 
  data, 
  index, 
  onSelect 
}: { 
  data: MarketData; 
  index: number; 
  onSelect?: (symbol: string) => void 
}) => {
  const isPositive = data.change >= 0;
  const getChangeColor = (change: number) => change >= 0 ? 'text-brand-teal-light' : 'text-brand-plum-light';
  const getChangeBgColor = (change: number) => change >= 0 ? 'bg-brand-teal/10' : 'bg-brand-plum/10';

  return (
    <motion.div 
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: (index % 12) * 0.04, duration: 0.5 }}
      whileHover={{ y: -4, scale: 1.01 }}
      onClick={() => onSelect?.(data.symbol)}
      className="relative bg-gradient-to-br from-[#121212] to-[#1A1A1A] p-10 rounded-2xl border border-white/5 hover:border-brand-teal/40 transition-all duration-500 group overflow-hidden cursor-pointer shadow-2xl"
    >
      <div className="flex justify-between items-start mb-10 relative z-10">
        <div className="space-y-1">
          <span className="text-text-primary font-black text-3xl tracking-tighter group-hover:text-brand-teal-light transition-colors duration-300">
            {data.symbol}
          </span>
          <p className="text-[10px] text-text-tertiary font-bold uppercase tracking-[0.2em] opacity-40">Asset Identification</p>
        </div>
        <div className={`px-4 py-1.5 rounded-full text-[11px] font-black tracking-widest bg-transparent border ${isPositive ? 'border-brand-teal/30 text-brand-teal-light' : 'border-brand-plum/30 text-brand-plum-light'} shadow-glow-sm`}>
          {Math.abs(data.changePercent).toFixed(2)}%
        </div>
      </div>
      
      <div className="space-y-10 relative z-10">
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col">
            <span className="text-text-tertiary text-[9px] font-black uppercase tracking-[0.2em] block mb-3 opacity-40">Neural Spot Price</span>
            <span className="text-white font-black text-3xl tracking-tighter">
              ${data.price > 0 ? (data.price < 1 ? data.price.toFixed(4) : data.price.toLocaleString(undefined, {minimumFractionDigits: 2})) : '0.00'}
            </span>
          </div>
          <div className="text-right flex flex-col items-end">
             <span className="text-text-tertiary text-[9px] font-black uppercase tracking-[0.2em] block mb-3 opacity-40">Institutional Delta</span>
             <span className={`font-black text-lg tracking-tight ${getChangeColor(data.change)}`}>
               {isPositive ? '+' : '-'}${data.change !== 0 ? Math.abs(data.change).toFixed(2) : '0.00'}
             </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
});

AssetCard.displayName = 'AssetCard';

export default function LiveMarketData({ 
  symbols,
  onSelectAsset
}: LiveMarketDataProps) {
  const { telemetry, isConnected } = useMarketStream(symbols);
  const [marketData, setMarketData] = useState<Record<string, MarketData>>({});
  const sparklineBuffers = useRef<Record<string, number[]>>({});

  useEffect(() => {
    // Initialize buffers and data structure if empty or symbols change
    const initial: Record<string, MarketData> = {};
    symbols.forEach(symbol => {
      initial[symbol] = marketData[symbol] || {
        symbol,
        price: 0,
        change: 0,
        changePercent: 0,
        volume: 0,
        high: 0,
        low: 0,
        open: 0,
        previousClose: 0,
        sparkline: []
      };
      if (!sparklineBuffers.current[symbol]) {
        sparklineBuffers.current[symbol] = [];
      }
    });
    setMarketData(initial);
  }, [symbols]);

  useEffect(() => {
    // Process telemetry updates with batching logic
    if (Object.keys(telemetry).length > 0) {
      setMarketData(prev => {
        const next = { ...prev };
        let hasChanges = false;
        
        Object.entries(telemetry).forEach(([symbol, update]) => {
          if (next[symbol]) {
            hasChanges = true;
            // Update sparkline buffer (keep last 30 points for better fidelity)
            const buffer = sparklineBuffers.current[symbol] || [];
            const newBuffer = [...buffer, update.price].slice(-30);
            sparklineBuffers.current[symbol] = newBuffer;

            next[symbol] = {
              ...next[symbol],
              price: update.price,
              change: update.change || next[symbol].change,
              changePercent: update.change_percent || next[symbol].changePercent,
              sparkline: newBuffer
            };
          }
        });
        
        return hasChanges ? next : prev;
      });
    }
  }, [telemetry]);

  const dataList = Object.values(marketData);

  return (
    <GlassCard variant="elevated" className="p-10 border-white/5 bg-bg-secondary/20 shadow-[0_0_80px_rgba(0,0,0,0.5)]">
      <div className="flex flex-col md:row justify-between items-start md:items-center mb-12 gap-6">
        <div className="space-y-2">
          <h3 className="text-md font-black uppercase tracking-[0.4em] text-text-tertiary flex items-center gap-3">
            <div className={`w-2.5 h-2.5 rounded-full ${isConnected ? 'bg-brand-teal shadow-glow animate-pulse' : 'bg-brand-plum shadow-[0_0_15px_rgba(158,36,255,0.5)]'}`} />
            Live Neural Intake
          </h3>
          <p className="text-[11px] text-text-tertiary font-bold uppercase tracking-[0.2em] opacity-40">
            Monitoring <span className="text-white">{symbols.length}</span> Sovereign Assets in Real-Time
          </p>
        </div>
        
        <div className="flex items-center gap-6">
          <div className="flex flex-col text-right hidden sm:flex">
             <span className="text-[10px] font-black text-text-tertiary uppercase tracking-widest opacity-60">Frequency</span>
             <span className="text-xs font-mono font-bold text-white tracking-widest">1,440Hz</span>
          </div>
          <div className={`flex items-center gap-3 px-5 py-2.5 bg-bg-secondary/60 backdrop-blur-2xl rounded-2xl border ${isConnected ? 'border-brand-teal/30' : 'border-brand-plum/30'} shadow-2xl transition-all duration-700`}>
             <div className={`w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-brand-teal animate-ping' : 'bg-brand-plum'}`} />
             <span className={`text-[10px] font-black tracking-[0.2em] uppercase ${isConnected ? 'text-brand-teal-light' : 'text-brand-plum-light'}`}>
               Status: {isConnected ? 'Synchronized' : 'Severed'}
             </span>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-8">
        <AnimatePresence mode="popLayout">
          {dataList.map((data, index) => (
            <AssetCard 
              key={data.symbol}
              data={data}
              index={index}
              onSelect={onSelectAsset}
            />
          ))}
        </AnimatePresence>
      </div>
    </GlassCard>
  );
}